// src/pages/ProfilePage.tsx
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiSave,
  FiCheckCircle,
  FiPhone,
  FiMail,
  FiMapPin,
  FiHome,
  FiUser,
  FiCalendar,
  FiFlag,
  FiFileText,
  FiShield,
  FiUploadCloud,
  FiAlertCircle,
  FiClock,
  FiXCircle,
} from 'react-icons/fi';
import { useAuth, User } from '../context/AuthContext';
import styles from './ProfilePage.module.css';
import { ProfileService, type KycDocType } from '../services/profile.service';
import { BankDetailsSection } from './BankDetailsPage';
import { ContactDataService } from '../services/contactData.service';
import { useKycDocuments } from '../hooks/useKycDocuments';
import {
  REQUIRED_KYC_DOC_TYPES,
  type RequiredKycDocType,
  parseOriginalNameFromNotes,
} from '../utils/kycDocuments';

interface SectionProps {
  user: User;
  setupMode: boolean;
}

interface CompleteProfileForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  state: string;
  postal_code: string;
  date_of_birth: string;
  nationality: string;
  rfc: string;
}

const isProfileComplete = (u: User) => !!u?.phone?.trim() && !!u?.email?.trim() && !!u?.address?.trim() && !!u?.city?.trim();

const PersonalInfoSection: React.FC<SectionProps> = ({ user, setupMode }) => {
  const { t } = useTranslation();
  const { refreshMe } = useAuth();

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  
  // Estados para los checkboxes de políticas
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [dataConsentAccepted, setDataConsentAccepted] = useState(false);

  // Initialize form with user data, ensuring all fields are populated
  const [form, setForm] = useState<CompleteProfileForm>({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    address: user?.address || '',
    city: user?.city || '',
    country: user?.country || 'México',
    state: user?.state || '',
    postal_code: user?.postal_code || '',
    date_of_birth: user?.date_of_birth || '',
    nationality: user?.nationality || 'Mexicana',
    rfc: user?.rfc || ''
  });

  // Update form when user data changes
  React.useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        email: user.email || '',
        address: user.address || '',
        city: user.city || '',
        country: user.country || 'México',
        state: user.state || '',
        postal_code: user.postal_code || '',
        date_of_birth: user.date_of_birth || '',
        nationality: user.nationality || 'Mexicana',
        rfc: user.rfc || ''
      });
    }
  }, [user]);

  const missingRequired = useMemo(() => {
    const firstNameOk = !!form.first_name?.trim();
    const lastNameOk = !!form.last_name?.trim();
    const phoneOk = !!form.phone?.trim();
    // Email is readonly, so we don't validate it here
    const addressOk = !!form.address?.trim();
    const cityOk = !!form.city?.trim();
    const countryOk = !!form.country?.trim();
    const stateOk = !!form.state?.trim();
    const postalCodeOk = !!form.postal_code?.trim();
    const dateOfBirthOk = !!form.date_of_birth?.trim();
    const nationalityOk = !!form.nationality?.trim();
    const rfcOk = !!form.rfc?.trim();
    const policiesOk = privacyAccepted && dataConsentAccepted;
    return !(firstNameOk && lastNameOk && phoneOk && addressOk && cityOk && countryOk && stateOk && postalCodeOk && dateOfBirthOk && nationalityOk && rfcOk && policiesOk);
  }, [form, privacyAccepted, dataConsentAccepted]);

  const handleSave = async () => {
    if (missingRequired) {
      setMsg({ type: 'info', text: 'Todos los campos marcados con * son obligatorios, incluyendo la aceptación de políticas' });
      return;
    }

    setSaving(true);
    setMsg(null);

    try {
      // Update profile data with all fields
      await ProfileService.updateMe({
        first_name: form.first_name?.trim(),
        last_name: form.last_name?.trim(),
        name: `${form.first_name?.trim()} ${form.last_name?.trim()}`,
        phone: form.phone?.trim(),
        address: form.address?.trim(),
        city: form.city?.trim(),
        rfc: form.rfc?.trim(),
        country: form.country?.trim(),
        state: form.state?.trim(),
        postal_code: form.postal_code?.trim(),
        date_of_birth: form.date_of_birth?.trim(),
        nationality: form.nationality?.trim()
      });

      // Update contact data (excluding email since it's readonly)
      await ContactDataService.updateContactData({
        phone: form.phone?.trim(),
        address: form.address?.trim(),
        city: form.city?.trim()
        // Note: email is not updated here as it's readonly
      });

      await refreshMe();
      setMsg({ type: 'success', text: 'Perfil actualizado exitosamente' });
    } catch (error) {
      console.error('Error updating profile:', error);
      setMsg({ type: 'error', text: 'Error al actualizar el perfil. Inténtalo de nuevo.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.surface}>
      {setupMode ? (
        <div className={styles.surfaceHeader}>
          <div className={styles.infoBox}>
            <FiCheckCircle size={18} />
            <p>{t('profile.setup_subtitle')}</p>
          </div>
        </div>
      ) : null}

      <div className={styles.contentArea}>
        <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
          Información del Perfil
        </h2>

        {/* Información Personal */}
        <div className={styles.formSection}>
          <h3 className={styles.subsectionTitle}>
            <FiUser /> Información Personal
          </h3>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Nombre(s) <span className={styles.required}>*</span></label>
              <div className={styles.inputWrap}>
                <FiUser className={styles.leadingIcon} />
                <input
                  className={styles.field}
                  value={form.first_name || ''}
                  onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                  placeholder="Tu nombre"
                  autoComplete="given-name"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Apellido <span className={styles.required}>*</span></label>
              <div className={styles.inputWrap}>
                <FiUser className={styles.leadingIcon} />
                <input
                  className={styles.field}
                  value={form.last_name || ''}
                  onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                  placeholder="Tu apellido"
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Fecha de Nacimiento <span className={styles.required}>*</span></label>
              <div className={styles.inputWrap}>
                <FiCalendar className={styles.leadingIcon} />
                <input
                  type="date"
                  className={styles.field}
                  value={form.date_of_birth || ''}
                  onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))}
                  autoComplete="bday"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Nacionalidad <span className={styles.required}>*</span></label>
              <div className={styles.inputWrap}>
                <FiFlag className={styles.leadingIcon} />
                <input
                  className={styles.field}
                  value={form.nationality || ''}
                  onChange={e => setForm(f => ({ ...f, nationality: e.target.value }))}
                  placeholder="Ej: Mexicana"
                  autoComplete="country"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Datos de Contacto */}
        <div className={styles.formSection}>
          <h3 className={styles.subsectionTitle}>
            <FiMail /> Datos de Contacto
          </h3>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Teléfono <span className={styles.required}>*</span></label>
              <div className={styles.inputWrap}>
                <FiPhone className={styles.leadingIcon} />
                <input
                  className={styles.field}
                  value={form.phone || ''}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+52 55 1234 5678"
                  autoComplete="tel"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Correo (solo lectura)</label>
              <div className={styles.inputWrap}>
                <FiMail className={styles.leadingIcon} />
                <input
                  className={`${styles.field} ${styles.readonlyField}`}
                  value={form.email || ''}
                  placeholder="correo@dominio.com"
                  autoComplete="email"
                  readOnly
                  disabled
                />
              </div>
              <small className={styles.hint}>El correo no se puede cambiar ya que es tu clave de acceso</small>
            </div>
          </div>
        </div>

        {/* Domicilio */}
        <div className={styles.formSection}>
          <h3 className={styles.subsectionTitle}>
            <FiMapPin /> Domicilio
          </h3>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label}>País <span className={styles.required}>*</span></label>
              <div className={styles.inputWrap}>
                <FiFlag className={styles.leadingIcon} />
                <input
                  className={styles.field}
                  value={form.country || ''}
                  onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                  placeholder="México"
                  autoComplete="country-name"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Estado <span className={styles.required}>*</span></label>
              <div className={styles.inputWrap}>
                <FiMapPin className={styles.leadingIcon} />
                <input
                  className={styles.field}
                  value={form.state || ''}
                  onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                  placeholder="Ciudad de México"
                  autoComplete="address-level1"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Ciudad <span className={styles.required}>*</span></label>
              <div className={styles.inputWrap}>
                <FiHome className={styles.leadingIcon} />
                <input
                  className={styles.field}
                  value={form.city || ''}
                  onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                  placeholder="Ciudad de México"
                  autoComplete="address-level2"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Código Postal <span className={styles.required}>*</span></label>
              <div className={styles.inputWrap}>
                <FiMapPin className={styles.leadingIcon} />
                <input
                  className={styles.field}
                  value={form.postal_code || ''}
                  onChange={e => setForm(f => ({ ...f, postal_code: e.target.value }))}
                  placeholder="03100"
                  maxLength={5}
                  autoComplete="postal-code"
                />
              </div>
            </div>

            <div className={styles.formGroupFull}>
              <label className={styles.label}>Dirección Completa <span className={styles.required}>*</span></label>
              <div className={styles.inputWrap}>
                <FiMapPin className={styles.leadingIcon} />
                <input
                  className={styles.field}
                  value={form.address || ''}
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  placeholder="Calle, número, colonia"
                  autoComplete="street-address"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Información Fiscal */}
        <div className={styles.formSection}>
          <h3 className={styles.subsectionTitle}>
            <FiFileText /> Información Fiscal
          </h3>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label}>RFC <span className={styles.required}>*</span></label>
              <div className={styles.inputWrap}>
                <FiFileText className={styles.leadingIcon} />
                <input
                  className={styles.field}
                  value={form.rfc || ''}
                  onChange={e => setForm(f => ({ ...f, rfc: e.target.value.toUpperCase() }))}
                  placeholder="ABCD123456XYZ"
                  maxLength={13}
                  autoComplete="off"
                />
              </div>
              <small className={styles.hint}>Registro Federal de Contribuyentes (13 caracteres)</small>
            </div>
          </div>
        </div>

        {/* Políticas y Consentimientos */}
        <div className={styles.formSection}>
          <h3 className={styles.subsectionTitle}>
            <FiShield /> Políticas y Consentimientos
          </h3>
          <div className={styles.policiesGrid}>
            <div className={styles.policyItem}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={privacyAccepted}
                  onChange={e => setPrivacyAccepted(e.target.checked)}
                />
                <span className={styles.checkmark}></span>
                <span className={styles.policyText}>
                  Acepto el <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className={styles.policyLink}>Aviso de Privacidad</a> <span className={styles.required}>*</span>
                </span>
              </label>
            </div>
            
            <div className={styles.policyItem}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={dataConsentAccepted}
                  onChange={e => setDataConsentAccepted(e.target.checked)}
                />
                <span className={styles.checkmark}></span>
                <span className={styles.policyText}>
                  Doy mi consentimiento para el uso y resguardo de mis datos personales, validación de identidad y consultas ante proveedores externos <span className={styles.required}>*</span>
                </span>
              </label>
            </div>
          </div>
        </div>

        {msg ? (
          <div className={styles.helperRow}>
            <p
              className={[
                styles.message,
                msg.type === 'success' ? styles.messageSuccess : '',
                msg.type === 'error' ? styles.messageError : ''
              ].join(' ')}
            >
              {msg.text}
            </p>
          </div>
        ) : null}

        <button onClick={handleSave} disabled={saving} className={styles.saveButton} type="button">
          <FiSave /> {saving ? t('common.saving') : t('common.save_changes')}
        </button>
      </div>
    </section>
  );
};

export const KycDocumentsSection: React.FC<{ user: User }> = ({ user }) => {
  const { profileStatus } = useAuth();
  const { statusMap, history, historyLoading, loading, error, refresh } = useKycDocuments({
    enabled: !!user,
  });
  const [uploadingType, setUploadingType] = useState<KycDocType | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const isActiveUser = profileStatus?.user_status ? profileStatus.user_status === 'ACTIVE' : true;
  const isProfileCompleted = profileStatus?.profile_completed ?? true;
  const canUpload = isActiveUser && isProfileCompleted;

  const latestByType = useMemo(() => {
    const map = new Map<RequiredKycDocType, { status: string; uploadedAt?: string; notes?: string }>();
    REQUIRED_KYC_DOC_TYPES.forEach((type) => {
      const item = statusMap.get(type);
      map.set(type, {
        status: item?.status ?? 'MISSING',
        uploadedAt: item?.uploadedAt,
        notes: item?.notes,
      });
    });
    return map;
  }, [statusMap]);

  const overallStatus = useMemo<'missing' | 'pending' | 'approved' | 'rejected'>(() => {
    const statuses = Array.from(latestByType.values()).map((doc) => doc.status);
    if (statuses.some((status) => status === 'MISSING')) return 'missing';
    if (statuses.some((status) => status === 'REJECTED')) return 'rejected';
    if (statuses.some((status) => status === 'PENDING')) return 'pending';
    if (statuses.every((status) => status === 'APPROVED')) return 'approved';
    return 'pending';
  }, [latestByType]);

  const overallMessage = useMemo(() => {
    if (overallStatus === 'missing') return 'Sube tus documentos para continuar con la validación KYC.';
    if (overallStatus === 'rejected') return 'Tus documentos fueron rechazados. Por favor, vuelve a cargarlos.';
    if (overallStatus === 'approved') return 'Tus documentos fueron validados.';
    return 'Tus documentos están en revisión.';
  }, [overallStatus]);

  const statusUI = (status?: string) => {
    const value = status || 'MISSING';
    if (value === 'APPROVED') return { label: 'Validado', className: styles.kycStatusApproved, icon: FiCheckCircle };
    if (value === 'REJECTED') return { label: 'Rechazado', className: styles.kycStatusRejected, icon: FiXCircle };
    if (value === 'PENDING') return { label: 'Pendiente', className: styles.kycStatusPending, icon: FiClock };
    return { label: 'Sin archivo', className: styles.kycStatusMissing, icon: FiAlertCircle };
  };

  const formatDate = (value?: string) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-MX');
  };

  const handleUpload = async (type: RequiredKycDocType, file?: File | null) => {
    if (!file) return;
    setMessage(null);

    if (!canUpload) {
      setMessage({ type: 'info', text: 'Completa tu perfil y mantén tu cuenta activa para subir documentos.' });
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Formato no permitido. Usa PDF, JPG o PNG.' });
      return;
    }

    if (file.size > maxSize) {
      setMessage({ type: 'error', text: 'El archivo excede el tamaño máximo permitido (10MB).' });
      return;
    }

    if (file.size <= 0) {
      setMessage({ type: 'error', text: 'El archivo seleccionado parece estar corrupto.' });
      return;
    }

    setUploadingType(type);
    try {
      await ProfileService.uploadKycDocument(type, file);
      await refresh();
      setMessage({ type: 'success', text: 'Documento cargado correctamente. Quedó en revisión.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'No se pudo subir el documento.' });
    } finally {
      setUploadingType(null);
    }
  };

  const documentsConfig: Array<{
    type: RequiredKycDocType;
    title: string;
    description: string;
    hint: string;
  }> = [
    {
      type: 'INE_FRONT',
      title: 'Identificación oficial (frente)',
      description: 'INE frente o pasaporte vigente.',
      hint: 'Asegúrate de que el texto sea legible.',
    },
    {
      type: 'INE_BACK',
      title: 'Identificación oficial (reverso)',
      description: 'INE reverso.',
      hint: 'Incluye la sección con domicilio y vigencia.',
    },
    {
      type: 'ADDRESS_PROOF',
      title: 'Comprobante de domicilio',
      description: 'Recibo de servicios o estado de cuenta (máx. 3 meses).',
      hint: 'Debe coincidir con tu información declarada.',
    },
  ];

  return (
    <section className={styles.surface}>
      <div className={styles.contentArea}>
        <div className={styles.sectionHeaderRow}>
          <div className={styles.sectionTitleRow}>
            <div className={styles.sectionIcon}>
              <FiShield />
            </div>
            <div>
              <h2 className={styles.sectionTitle}>Verificación de identidad (KYC)</h2>
              <p className={styles.sectionSubtitle}>
                Sube tus documentos oficiales para validar tu identidad conforme a CNBV/PLD. Formatos permitidos: PDF, JPG, PNG (máx. 10 MB).
              </p>
            </div>
          </div>
        </div>

        {!isProfileCompleted ? (
          <div className={`${styles.alertBox} ${styles.alertInfo}`}>
            <FiAlertCircle />
            <p>Completa tu perfil antes de cargar documentos.</p>
          </div>
        ) : null}

        {!isActiveUser ? (
          <div className={`${styles.alertBox} ${styles.alertInfo}`}>
            <FiAlertCircle />
            <p>Tu cuenta debe estar activa para cargar documentos KYC.</p>
          </div>
        ) : null}

        {loading ? (
          <div className={styles.kycLoading}>Cargando documentos...</div>
        ) : error ? (
          <div className={`${styles.alertBox} ${styles.alertError}`}>
            <FiAlertCircle />
            <p>{error}</p>
          </div>
        ) : (
          <div
            className={`${styles.alertBox} ${
              overallStatus === 'approved'
                ? styles.alertSuccess
                : overallStatus === 'rejected'
                  ? styles.alertError
                  : styles.alertInfo
            }`}
          >
            {overallStatus === 'approved' ? <FiCheckCircle /> : overallStatus === 'rejected' ? <FiXCircle /> : <FiClock />}
            <p>{overallMessage}</p>
          </div>
        )}

        {message ? (
          <div
            className={`${styles.alertBox} ${
              message.type === 'success'
                ? styles.alertSuccess
                : message.type === 'error'
                  ? styles.alertError
                  : styles.alertInfo
            }`}
          >
            <FiAlertCircle />
            <p>{message.text}</p>
          </div>
        ) : null}

        <div className={styles.kycGrid}>
          {documentsConfig.map((doc) => {
            const current = latestByType.get(doc.type);
            const status = statusUI(current?.status);
            const StatusIcon = status.icon;
            const historyItems = history[doc.type] ?? [];
            const canReplace = !current || current.status === 'MISSING' || current.status === 'REJECTED';
            const uploadDisabled = !canUpload || uploadingType === doc.type || !canReplace;
            const originalName = parseOriginalNameFromNotes(current?.notes);

            return (
              <div key={doc.type} className={styles.kycCard}>
                <div className={styles.kycCardHeader}>
                  <div>
                    <div className={styles.kycTitle}>{doc.title}</div>
                    <div className={styles.kycHint}>{doc.description}</div>
                  </div>
                  <span className={`${styles.kycStatus} ${status.className}`}>
                    <StatusIcon size={14} /> {status.label}
                  </span>
                </div>

                <div className={styles.kycMeta}>
                  <div>
                    <span className={styles.kycMetaLabel}>Última carga:</span>{' '}
                    <span>{current?.uploadedAt ? formatDate(current.uploadedAt) : '—'}</span>
                  </div>
                  <div>
                    <span className={styles.kycMetaLabel}>Archivo:</span>{' '}
                    <span>{originalName || '—'}</span>
                  </div>
                </div>

                <div className={styles.kycNote}>{doc.hint}</div>

                <div className={styles.kycActions}>
                  <label className={`${styles.kycButton} ${uploadDisabled ? styles.kycButtonDisabled : ''}`}>
                    <FiUploadCloud />
                    {uploadingType === doc.type
                      ? 'Subiendo...'
                      : current && current.status !== 'MISSING'
                        ? current.status === 'REJECTED'
                          ? 'Reemplazar documento'
                          : 'Documento cargado'
                        : 'Subir documento'}
                    <input
                      type="file"
                      className={styles.kycInput}
                      accept=".jpg,.jpeg,.png,.pdf"
                      disabled={uploadDisabled}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        handleUpload(doc.type, file);
                        e.currentTarget.value = '';
                      }}
                    />
                  </label>
                </div>

                {historyItems.length > 0 ? (
                  <details className={styles.kycHistory}>
                    <summary>Historial de cargas</summary>
                    <ul className={styles.kycHistoryList}>
                      {historyLoading[doc.type] ? (
                        <li className={styles.kycHistoryItem}>
                          <span>Cargando historial...</span>
                          <span>—</span>
                          <span>—</span>
                        </li>
                      ) : (
                        historyItems.map((item) => {
                          const itemName = parseOriginalNameFromNotes(item.notes);
                          return (
                            <li key={String(item.id)} className={styles.kycHistoryItem}>
                              <span>{itemName || 'Documento'}</span>
                              <span>{formatDate(item.uploadedAt)}</span>
                              <span>{statusUI(item.status).label}</span>
                            </li>
                          );
                        })
                      )}
                    </ul>
                  </details>
                ) : null}
              </div>
            );
          })}
        </div>

        {overallStatus === 'missing' && !loading ? (
          <p className={styles.kycFooterNote}>
            Recuerda: sin documentos cargados no podrás invertir ni iniciar nuevos proyectos.
          </p>
        ) : null}
      </div>
    </section>
  );
};

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  if (!user) return null;

  const setupMode = user.role === 'client' && !isProfileComplete(user);

  return (
    <div className={styles.pageContainer}>
      <h1 className={styles.pageTitle}>{setupMode ? t('profile.setup_title') : t('profile.page_title')}</h1>
      <p className={styles.subTitle}>
        {setupMode ? t('profile.setup_subtitle') : t('profile.page_subtitle')}
      </p>

      <div style={{ display: 'grid', gap: 18 }}>
        <PersonalInfoSection user={user} setupMode={setupMode} />

        <section className={styles.surface}>
          <div className={styles.contentArea}>
            <BankDetailsSection />
          </div>
        </section>

        <KycDocumentsSection user={user} />
      </div>
    </div>
  );
};

export default ProfilePage;
